import { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Pencil, ShoppingCart, ClipboardList, Route } from "lucide-react"
import MDBox from "components/MDBox"
import { Page, Card, Button, Loading, ProcessStepper } from "./ui"
import SavedOrderBudgetAllocation from "./components/orders/SavedOrderBudgetAllocation"
import { collectActionNotes } from "./components/ActionNotes"
import StatusBadge from "./components/StatusBadge"
import {
  VendorProposalsSection,
  OrderLinesSection,
  LinkedPurchasesSection,
  AttachmentsSection,
  ApprovalProgressSection,
} from "./components/approvals/ManagerOrderSections"
import DecisionCard, {
  DecisionButtons,
  ConfirmModal,
  allowed,
} from "./components/approvals/DecisionCard"
import DeliveryConfirmationCard from "./components/approvals/DeliveryConfirmationCard"
import OverBudgetBanner from "./components/orders/OverBudgetBanner"
import CloseOrderButton from "./components/orders/CloseOrderButton"
import {
  useGetOrderReviewQuery,
  useSubmitOrderActionMutation,
  usePreviewOrderApprovalQuery,
} from "shared/redux/endpoints/procurementApprovals"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { ORDER_STATUS, canOpenOrderEdit } from "./status"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import {
  PAYMENT_METHOD_OPTIONS,
  formatVND,
  formatDate,
  formatOrderFor,
  approvalLimitExceededMessage,
  approvalModeLabel,
  formatQuorum,
} from "./constants"
import { ApprovalRoute } from "./ui"
import "./ui/tokens.css"

// "Goods received" is a milestone on the linked PO (delivery_status), not a
// real OR status — the OR itself stays `approved` while that happens (see
// `stepperKey` below). It used to reuse ORDER_STATUS.INFO_REQUESTED as a
// stand-in key for that step, which collided with the real info_requested
// status: whenever an order was genuinely waiting on the requester for more
// info, the stepper matched it to this step instead and rendered Approved
// (and everything before it) as done — implying the order had already
// cleared approval when it hadn't. Use a synthetic key that can never equal
// a real ORDER_STATUS value instead.
const GOODS_RECEIVED_STEP_KEY = "goods_received"

const ORDER_STEPS = [
  { key: ORDER_STATUS.DRAFT, label: "Draft" },
  { key: ORDER_STATUS.INFO_REQUESTED, label: "Info requested" },
  { key: ORDER_STATUS.PENDING, label: "Waiting for approval" },
  { key: ORDER_STATUS.APPROVED, label: "Approved" },
  { key: GOODS_RECEIVED_STEP_KEY, label: "Goods received" },
  { key: ORDER_STATUS.CLOSED, label: "Closed" },
]

const paymentMethodLabel = (code) =>
  PAYMENT_METHOD_OPTIONS.find((o) => o.value === code)?.label || code || "—"

// Read-only label/value pair (muted label above a strong value).
const Field = ({ label, value, full, strong }) => (
  <div className={`pc-def${full ? " pc-def-full" : ""}`}>
    <div className="pc-def-label">{label}</div>
    <div className={`pc-def-value${strong ? " pc-def-value--strong" : ""}`}>{value ?? "—"}</div>
  </div>
)

const GroupLabel = ({ children }) => <div className="pc-def-group pc-def-full">{children}</div>

const OrderReviewContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: order, isLoading, isError, refetch } = useGetOrderReviewQuery(id, { skip: !id })
  const [submitAction] = useSubmitOrderActionMutation()
  // Order lines only carry a bare centerId — resolve to a display name for
  // the read-only lines table below (same data the create/edit form shows).
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerNameById = useMemo(() => {
    const map = {}
    ;(centersPage?.items || []).forEach((c) => {
      if (c.centerId != null) map[c.centerId] = c.centerName
    })
    return map
  }, [centersPage])

  // Approval route resolved from the rule engine (who's on the route for this
  // category/region/amount) — the live per-step state is in Approval Progress.
  const { data: routePreview } = usePreviewOrderApprovalQuery(
    {
      entity: "order_request",
      category: order?.budgetCategoryId,
      region: order?.region,
      amount: order?.total,
    },
    { skip: !order?.budgetCategoryId }
  )
  const routeSteps = useMemo(
    () =>
      (routePreview?.steps || []).map((s) => ({
        ...s,
        approverLabel: Array.isArray(s.approverNames)
          ? s.approverNames.join(", ")
          : s.approverLabel || s.approvers || "—",
        modeLabel: `${approvalModeLabel(s.mode)} · ${formatQuorum(s.quorum)}`,
      })),
    [routePreview]
  )

  const [activeAction, setActiveAction] = useState(null)
  const [busy, setBusy] = useState(false)
  const [locked, setLocked] = useState(false) // set on 403

  // Plain history back — this page is reachable from the approvals inbox, the
  // org-wide Order list, My Orders, or a direct link, so don't force a
  // destination.
  const back = () => navigate(-1)

  const onConfirm = async (note, deadline) => {
    if (busy || !activeAction) return
    setBusy(true)
    try {
      const result = await submitAction({ id, action: activeAction, note, deadline }).unwrap()
      // A 200 here only means the call went through — if the info-request
      // deadline had already passed, the backend auto-rejects instead, and a
      // green success toast would misreport that.
      if (isAutoRejectedOutcome(activeAction, result, ORDER_STATUS.REJECTED)) {
        showToastError(AUTO_REJECT_MESSAGE)
      } else {
        showToastSuccess("Your decision was submitted.")
      }
      setActiveAction(null)
      refetch()
    } catch (e) {
      const status = e?.status
      if (status === 409) {
        showToastError("This order was already updated.")
        setActiveAction(null)
        refetch()
      } else if (status === 403) {
        const limitMsg = approvalLimitExceededMessage(
          e?.data?.data?.message || e?.data?.message
        )
        showToastError(limitMsg || "You're not allowed to act on this order.")
        setActiveAction(null)
        setLocked(true)
      } else {
        const msg = e?.data?.data?.message || e?.data?.message || "Couldn't submit your decision. Please try again."
        showToastError(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const crumbs = ["Procurement", "Approvals", order?.orCode || id]

  if (isLoading) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={crumbs} title={order?.orCode || "Order"}>
        <Loading />
      </Page>
    )
  }

  if (isError || !order) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={crumbs} title="Order">
        <Card>
          <div className="pc-section-empty">
            <div className="pc-section-empty-title">Couldn&apos;t load this order</div>
            The order request couldn&apos;t be loaded. It may have been removed, or the service is
            unavailable.
            <div style={{ marginTop: 12 }}>
              <Button variant="secondary" size="sm" onClick={refetch}>
                Retry
              </Button>
              <Button variant="ghost" size="sm" onClick={back}>
                Back
              </Button>
            </div>
          </div>
        </Card>
      </Page>
    )
  }

  const allowedActions = order.allowedActions || []
  const cardDisabled = locked
  const hasActions = !cardDisabled && allowed(allowedActions).length > 0
  const canEdit = canOpenOrderEdit(order)
  // "Goods received" is a milestone on the PO (delivery_status), not an OR
  // status — the OR stays `approved`. Light up that step once any linked PO
  // is marked received, same override OrderDetailContainer uses.
  const deliveryReceived = (order.purchases || []).some((p) => p.deliveryStatus === "received")
  const stepperKey =
    order.status === ORDER_STATUS.APPROVED && deliveryReceived
      ? GOODS_RECEIVED_STEP_KEY
      : order.status

  return (
    <Page
      className="pc-page--refined pc-page--detail"
      breadcrumb={crumbs}
      title={
        <span className="pc-title-row">
          {order.orCode}
          <StatusBadge status={order.status} />
        </span>
      }
      subtitle="Review this request and record your decision."
      actions={
        <>
          <Button variant="secondary" onClick={back}>
            Back
          </Button>
          {canEdit && (
            <Button
              variant="secondary"
              leftIcon={<Pencil aria-hidden />}
              onClick={() => navigate(`/procurement/order-requests/${order.id}/edit`)}
            >
              Edit
            </Button>
          )}
          <CloseOrderButton order={order} onClosed={refetch} />
          {order.status === ORDER_STATUS.APPROVED && (
            <Button
              variant="primary"
              leftIcon={<ShoppingCart aria-hidden />}
              onClick={() => navigate(`/procurement/purchases/new?order_id=${order.id}`)}
            >
              Create PO
            </Button>
          )}
        </>
      }
    >
      <OverBudgetBanner order={order} />

      <Card className="pc-progress-card">
        <ProcessStepper steps={ORDER_STEPS} currentKey={stepperKey} />
      </Card>

      <div className="pc-review">
        <div className="pc-review-main">
          <Card icon={<ClipboardList aria-hidden />} title="Order Information" subtitle={order.orCode}>
            <div className="pc-defgrid">
              <GroupLabel>Request information</GroupLabel>
              <Field label="Request date" value={formatDate(order.requestDate)} />
              <Field label="Order for" value={formatOrderFor(order.orderForType, order.orderForDates)} />
              <Field
                label="Requester"
                value={
                  <>
                    {order.requesterName || "—"}
                    {(order.requesterCenter || order.requesterDepartment) && (
                      <span className="pc-def-sub">
                        {[order.requesterCenter, order.requesterDepartment].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </>
                }
              />
              <Field label="Category" value={order.categoryName || order.category} />
              <Field label="Region" value={order.region} />
              <Field
                label="Urgency"
                value={<span style={{ textTransform: "capitalize" }}>{order.urgency || "normal"}</span>}
              />

              <GroupLabel>Financial information</GroupLabel>
              <Field label="Payment method" value={paymentMethodLabel(order.paymentMethod)} />
              <Field label="Allocation info" value={order.allocationInfo} />
              <Field label="Total amount" value={formatVND(order.total)} strong full />

              <GroupLabel>Reason</GroupLabel>
              <Field label="Reason for request" value={order.reason} full />
            </div>
          </Card>

          <SavedOrderBudgetAllocation order={order} centerNameById={centerNameById} />
          <OrderLinesSection lines={order.lines} centerNameById={centerNameById} />
          <VendorProposalsSection proposals={order.vendorProposals} lines={order.lines} sourceId={order.id || id} />
          <LinkedPurchasesSection purchases={order.purchases} />
          <DeliveryConfirmationCard order={order} orderId={id} />
          <AttachmentsSection documents={order.documents} />
          {routeSteps.length > 0 && (
            <Card icon={<Route aria-hidden />} title="Approval Route Preview">
              {routePreview?.ruleName && (
                <p className="pc-hint" style={{ marginBottom: 12 }}>
                  Matched rule: <strong>{routePreview.ruleName}</strong>
                  {routePreview.version ? ` (v${routePreview.version})` : ""}
                </p>
              )}
              <ApprovalRoute steps={routeSteps} />
            </Card>
          )}
          <ApprovalProgressSection steps={order.approvalProgress} />
        </div>

        {/* Decision sidebar */}
        <aside className="pc-review-side">
          <DecisionCard
            allowedActions={allowedActions}
            disabled={cardDisabled}
            statusMessage={order.decisionMessage}
            notes={collectActionNotes(order.actionLog, order.approvalProgress)}
            onPick={setActiveAction}
          />
        </aside>
      </div>

      {/* Mobile sticky action bar (mirrors the decision card) */}
      {hasActions && (
        <MDBox
          sx={{
            display: { xs: "flex", md: "none" },
            position: "sticky",
            bottom: 0,
            zIndex: 20,
            gap: 1,
            p: 1.5,
            backgroundColor: "white",
            borderTop: "1px solid",
            borderColor: "grey.300",
            boxShadow: "0 -4px 12px rgba(15, 23, 42, 0.08)",
          }}
        >
          <DecisionButtons actions={allowed(allowedActions)} onPick={setActiveAction} block={false} />
        </MDBox>
      )}

      {activeAction && (
        <ConfirmModal
          actionKey={activeAction}
          order={order}
          busy={busy}
          onCancel={() => !busy && setActiveAction(null)}
          onSubmit={onConfirm}
        />
      )}
    </Page>
  )
}

export default OrderReviewContainer
